import React, { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getNotifications, markAsRead, markAllAsRead, Notification } from "../services/notifications";
import { getPointsSummary, PointsSummary } from "../services/api";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import UpdateAvailableBanner from "../components/UpdateAvailableBanner";
import ClubPausedNotice from "../components/ClubPausedNotice";
import OnboardingTour, { hasSeenOnboarding } from "../components/OnboardingTour";
import RoleModeSwitcher from "../components/RoleModeSwitcher";
import { applyAndroidNavInset } from "../lib/androidEdgeToEdge";

// Small amber star = points glyph (never coins — kids app).
const StarGlyph = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path d="M12 2.5l2.9 5.9 6.5.95-4.7 4.6 1.1 6.45L12 17.9l-5.8 3 1.1-6.45-4.7-4.6 6.5-.95L12 2.5Z" fill="currentColor" />
  </svg>
);

interface MobileAppLayoutProps {
  children: React.ReactNode;
}

// Navigation items are now built inside the component so labels go through
// i18n. They used to be module-level constants with hardcoded English/Romanian
// strings, which is how RO athletes ended up seeing "Sessions / Quizzes /
// Leaderboard" in the bottom bar regardless of their locale setting.
type NavItem = { path: string; icon: string; labelKey: string; labelFallback: string };
const athleteNavItemsBase: NavItem[] = [
  { path: "/app/dashboard", icon: "home", labelKey: "nav.dashboard", labelFallback: "Acasă" },
  { path: "/app/sessions", icon: "calendar", labelKey: "nav.sessions", labelFallback: "Sesiuni" },
  { path: "/app/events", icon: "event", labelKey: "nav.events", labelFallback: "Evenimente" },
  { path: "/app/quizzes", icon: "quiz", labelKey: "nav.quizzes", labelFallback: "Quiz-uri" },
  { path: "/app/leaderboard", icon: "trophy", labelKey: "nav.leaderboard", labelFallback: "Clasament" },
];

const coachNavItemsBase: NavItem[] = [
  { path: "/app/coach", icon: "home", labelKey: "nav.home", labelFallback: "Acasă" },
  { path: "/app/coach/sessions", icon: "calendar", labelKey: "nav.sessions", labelFallback: "Sesiuni" },
  { path: "/app/coach/teams", icon: "team", labelKey: "nav.teams", labelFallback: "Echipe" },
  { path: "/app/coach/attendance", icon: "clipboard", labelKey: "nav.attendance", labelFallback: "Prezență" },
  { path: "/app/coach/events", icon: "event", labelKey: "nav.events", labelFallback: "Evenimente" },
];

const parentNavItemsBase: NavItem[] = [
  { path: "/app/dashboard", icon: "home", labelKey: "nav.dashboard", labelFallback: "Acasă" },
  { path: "/app/parent/sessions", icon: "calendar", labelKey: "nav.trainings", labelFallback: "Antrenamente" },
  { path: "/app/parent/events", icon: "event", labelKey: "nav.events", labelFallback: "Evenimente" },
  { path: "/app/parent/attendance", icon: "check", labelKey: "nav.attendance", labelFallback: "Prezență" },
  { path: "/app/parent/points", icon: "points", labelKey: "nav.points", labelFallback: "Puncte" },
];

// Nav for a parent whose selected child is "individual" (no real club).
// Trainings / events / attendance don't exist for such a child, so we swap
// them for the surfaces that DO work: learning progress, points and lives.
const parentIndividualNavItemsBase: NavItem[] = [
  { path: "/app/dashboard", icon: "home", labelKey: "nav.dashboard", labelFallback: "Acasă" },
  { path: "/app/parent/progress", icon: "trophy", labelKey: "nav.progress", labelFallback: "Progres" },
  { path: "/app/parent/points", icon: "points", labelKey: "nav.points", labelFallback: "Puncte" },
  { path: "/app/lives", icon: "heart", labelKey: "nav.lives", labelFallback: "Vieți" },
  { path: "/app/settings", icon: "cog", labelKey: "nav.settings", labelFallback: "Setări" },
];

const NavIcon = ({ icon, active }: { icon: string; active: boolean }) => {
  // Inactive glyphs sit on a near-black bar now, so slate-500 was too dim
  // to read; slate-300 keeps the active/inactive contrast without vanishing.
  const baseClass = `w-[23px] h-[23px] transition-all duration-200 ${active ? "text-indigo-300 drop-shadow-[0_4px_10px_rgba(99,102,241,.55)]" : "text-slate-300"}`;

  switch (icon) {
    case "home":
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      );
    case "calendar":
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    case "quiz":
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      );
    case "trophy":
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      );
    case "users":
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      );
    case "check":
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "team":
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      );
    case "clipboard":
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      );
    case "points":
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "event":
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          <circle cx="8" cy="14" r="1" fill="currentColor" />
          <circle cx="12" cy="14" r="1" fill="currentColor" />
          <circle cx="16" cy="14" r="1" fill="currentColor" />
        </svg>
      );
    case "heart":
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      );
    case "cog":
      return (
        <svg className={baseClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    default:
      return null;
  }
};

export default function MobileAppLayout({ children }: MobileAppLayoutProps) {
  const { user, activeRole, clubs, activeClubId, logout, activeChildIndividual } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  // Athlete HUD in the header — points + level. Only fetched for athletes.
  const [pts, setPts] = useState<PointsSummary | null>(null);
  const isAthlete = activeRole !== "coach" && activeRole !== "parent";
  // First-run walkthrough (athletes only, shown once).
  const [showTour, setShowTour] = useState(isAthlete && !hasSeenOnboarding());
  const profileRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const mainScrollRef = useRef<HTMLElement>(null);

  // Lock the document scroll while this layout is mounted. Applying
  // overflow-hidden on the outer div isn't enough on iOS — Safari
  // WebView still bounces the html/body layer. The class in
  // index.css pins html + body to the visual viewport with fixed
  // positioning, so only <main> can scroll. Detached on unmount so
  // the web/admin routes keep normal document scroll.
  useEffect(() => {
    document.documentElement.classList.add("mobile-app-scroll-lock");
    // Keep the bottom nav clear of Android's system navigation bar on
    // edge-to-edge builds that don't report the inset (see the helper).
    applyAndroidNavInset();
    return () => {
      document.documentElement.classList.remove("mobile-app-scroll-lock");
    };
  }, []);

  // Soft-keyboard handling.
  //
  // The scroll lock above pins html/body to a 100vh box, and 100vh does NOT
  // shrink when the on-screen keyboard opens. The browser's only way to reveal
  // a focused field was to shove the whole pinned layer upwards, which took
  // the field being typed into off screen with it. So we measure the visible
  // area ourselves (visualViewport) and shrink the shell by the keyboard's
  // height instead — <main> then scrolls normally and the field stays put.
  const [keyboardInset, setKeyboardInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const sync = () => {
      const hidden = window.innerHeight - vv.height - vv.offsetTop;
      // Address bars and toolbars also change this by a few dozen px; only a
      // sizeable gap is a keyboard.
      setKeyboardInset(hidden > 120 ? Math.round(hidden) : 0);
    };
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    sync();
    return () => {
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
    };
  }, []);
  const keyboardOpen = keyboardInset > 0;

  // Bring the focused field into view once the keyboard animation and our own
  // resize have settled. Without this the field can sit just under the fold of
  // the newly shortened viewport.
  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;
    let timer: number | undefined;
    const onFocusIn = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target?.tagName) return;
      if (!["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        target.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 350);
    };
    el.addEventListener("focusin", onFocusIn);
    return () => {
      window.clearTimeout(timer);
      el.removeEventListener("focusin", onFocusIn);
    };
  }, []);

  // Pull-to-refresh wired up to the <main> scroll container. On
  // threshold release we just reload the WebView — with live-load
  // (capacitor.config.ts server.url) this re-fetches the latest
  // bundle from app.sportiveducat.ro and rebuilds React state from
  // scratch.
  //
  // Deliberately limited to Home and Clasament. It used to run on every
  // page in this layout, which meant a stray downward drag anywhere
  // (a form, a quiz, a modal-heavy list) blew the whole WebView away.
  // Those are the only two screens whose content changes under the user
  // without any action on their part.
  const ptrPaths = ["/app/dashboard", "/app/coach", "/app/leaderboard"];
  const pullToRefreshEnabled = ptrPaths.includes(location.pathname.replace(/\/$/, ""));
  usePullToRefresh(mainScrollRef, () => window.location.reload(), {
    enabled: pullToRefreshEnabled,
  });

  // Determine which nav items to show based on role, and translate labels
  // through i18n so the bottom bar respects the user's locale (the previous
  // implementation hardcoded English labels for the athlete nav).
  const navItemsBase = activeRole === "coach"
    ? coachNavItemsBase
    : activeRole === "parent"
      ? (activeChildIndividual ? parentIndividualNavItemsBase : parentNavItemsBase)
      : athleteNavItemsBase;
  const navItems = navItemsBase.map(item => ({
    path: item.path,
    icon: item.icon,
    label: t(item.labelKey, item.labelFallback),
  }));

  // Athlete points + level for the header HUD.
  useEffect(() => {
    if (!isAthlete) return;
    getPointsSummary()
      .then(setPts)
      .catch(() => {});
  }, [isAthlete]);

  // Index of the active nav item (drives the sliding indicator).
  const activeIndex = navItems.findIndex(
    (item) =>
      location.pathname === item.path ||
      (item.path !== "/app/dashboard" &&
        item.path !== "/app/coach" &&
        location.pathname.startsWith(item.path))
  );

  // Get current club info
  const currentClub = clubs.find(c => c.id === activeClubId);
  // Premium: the club's brand color becomes the app accent for its members.
  const brandColor = currentClub?.brand_color || null;

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const data = await getNotifications({ limit: 10 });
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    // Refresh immediately when a push notification arrives in foreground
    const onPush = () => fetchNotifications();
    window.addEventListener('push-received', onPush);
    return () => {
      clearInterval(interval);
      window.removeEventListener('push-received', onPush);
    };
  }, [fetchNotifications]);

  const handleMarkAsRead = async (notification: Notification) => {
    if (!notification.read_at) {
      try {
        await markAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error("Failed to mark as read:", err);
      }
    }
    setNotificationsOpen(false);
    if (notification.payload?.action_url) {
      navigate(notification.payload.action_url);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "acum";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Get user initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    // html + body are now pinned to the visual viewport by the
    // mobile-app-scroll-lock class (added on mount in useEffect
    // above), so this outer div just fills that fixed parent with
    // h-full. That's the piece that stops iOS from rubber-banding
    // the whole document — without the html/body pin, overflow-
    // hidden here alone still let Safari bounce the layer above,
    // dragging header + main + footer out of the visual viewport.
    <div
      className="flex flex-col h-full overflow-hidden bg-slate-50"
      style={keyboardOpen ? { height: `calc(100% - ${keyboardInset}px)` } : undefined}
    >
      {/* First-run walkthrough (athletes only) */}
      {showTour && isAthlete && <OnboardingTour onDone={() => setShowTour(false)} />}

      {/* Top Header */}
      <header
        className="sticky top-0 z-50 px-4 pb-3 bg-gradient-to-b from-[#131a2e] to-[#0b1120] safe-area-top"
        style={brandColor ? ({ "--brand-accent": brandColor, boxShadow: `inset 0 -2px 0 ${brandColor}` } as React.CSSProperties) : undefined}
      >
        <div className="flex items-center justify-between">
          {/* Left spacer for balance */}

          <div className="flex items-center gap-2">
            {/* Club Logo */}
            <Link to={activeRole === "coach" ? "/app/coach" : "/app/dashboard"} className="flex-shrink-0">
              {currentClub?.logo_url ? (
                <img
                  src={currentClub.logo_url}
                  alt={currentClub.name}
                  className="object-cover border rounded-lg w-9 h-9 border-slate-600"
                />
              ) : (
                <div
                  className="flex items-center justify-center text-sm font-bold text-white border border-indigo-400 rounded-lg w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-600"
                  style={brandColor ? { background: brandColor, borderColor: brandColor } : undefined}
                >
                  {currentClub?.name?.charAt(0) || 'C'}
                </div>
              )}
            </Link>
            {/* Tapping the greeting/name opens the user's profile — kids
                instinctively tap here expecting their profile & settings. */}
            <Link to={activeRole === "coach" ? "/app/coach/profile" : activeRole === "parent" ? "/app/settings" : "/app/profile"} className="flex-shrink-0">
              <img src="/assets/ig_h_color.png" alt="iGrowth" className="hidden w-56 h-auto" />

              <div className="flex-1 text-left leading-tight">
                <p className="text-[11px] font-semibold text-slate-400">{t("nav.greeting", "Bună,")}</p>
                <h1 className="text-[15px] font-bold text-white -mt-0.5">
                  {user?.full_name?.split(' ')[0] || 'Utilizator'}
                </h1>
              </div>
            </Link>
          </div>

          {/* Center: Greeting */}
          

          {/* Right Side: HUD + Notifications + Profile */}
          <div className="flex items-center gap-2">
            {/* Points HUD (athletes) */}
            {isAthlete && pts && (
              <span className="inline-flex items-center gap-1 text-amber-300 text-[13px] font-extrabold bg-amber-400/10 border border-amber-400/20 rounded-full px-2.5 py-1 tabular-nums">
                <StarGlyph className="w-3.5 h-3.5" />
                {pts.total.toLocaleString("ro-RO")}
              </span>
            )}
            {/* Notifications */}
            <div ref={notificationsRef} className="relative">
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative w-9 h-9 grid place-items-center rounded-xl bg-white/5 border border-white/[0.06] text-slate-400 hover:text-white hover:bg-white/[0.08] transition-colors"
              >
                <svg className="w-[19px] h-[19px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full ring-2 ring-[#0d1526]" />
                )}
              </button>

              {/* Notifications Dropdown */}
              {notificationsOpen && (
                <div className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border shadow-xl w-80 rounded-xl border-slate-200">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <span className="font-semibold text-slate-800">{t("notifications.title", "Notificări")}</span>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllAsRead}
                        className="text-xs text-indigo-600 hover:text-indigo-700"
                      >
                        {t("notifications.mark_all_read", "Marchează toate ca citite")}
                      </button>
                    )}
                  </div>
                  <div className="overflow-y-auto max-h-72">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-sm text-center text-slate-500">
                        {t("notifications.no_notifications", "Nicio notificare")}
                      </div>
                    ) : (
                      notifications.map(notification => (
                        <button
                          key={notification.id}
                          onClick={() => handleMarkAsRead(notification)}
                          className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                            !notification.read_at ? "bg-indigo-50/50" : ""
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                              !notification.read_at ? "bg-indigo-500" : "bg-transparent"
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate text-slate-800">
                                {notification.title}
                              </p>
                              <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                                {notification.payload?.message}
                              </p>
                              <p className="mt-1 text-xs text-slate-400">
                                {formatTimeAgo(notification.created_at)}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Profile Button */}
            <div ref={profileRef} className="relative">
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="relative rounded-full p-0.5 transition-colors hover:bg-white/10"
              >
                <div className="relative w-9 h-9">
                  {(user?.profile_url || user?.avatar_url) ? (
                    <img
                      src={user.profile_url || user.avatar_url || undefined}
                      alt={user.full_name || undefined}
                      className="object-cover rounded-full w-9 h-9 ring-1 ring-white/15"
                    />
                  ) : (
                    <div className="flex items-center justify-center text-sm font-semibold text-white rounded-full w-9 h-9 ring-1 ring-white/15 bg-gradient-to-br from-violet-500 to-fuchsia-600">
                      {getInitials(user?.full_name || "U")}
                    </div>
                  )}
                  {isAthlete && pts?.level && (
                    <>
                      <svg className="absolute -inset-[3px] w-[42px] h-[42px] -rotate-90" viewBox="0 0 42 42">
                        <circle cx="21" cy="21" r="19.5" fill="none" stroke="rgba(255,255,255,.14)" strokeWidth="2.5" />
                        <circle
                          cx="21" cy="21" r="19.5" fill="none" stroke="#fbbf24" strokeWidth="2.5" strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 19.5}
                          strokeDashoffset={(2 * Math.PI * 19.5) * (1 - (pts.level.progress_pct || 0) / 100)}
                        />
                      </svg>
                      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] font-black text-white bg-indigo-500 rounded-full px-1 leading-[14px] ring-2 ring-[#0d1526]">
                        {pts.level.level}
                      </span>
                    </>
                  )}
                </div>
              </button>

              {/* Profile Menu Dropdown */}
              {profileMenuOpen && (
                <div className="absolute right-0 z-50 w-64 mt-2 overflow-hidden bg-white border shadow-xl rounded-xl border-slate-200">
                  {/* User Info — tapping avatar/name opens my profile */}
                  <Link
                    to={activeRole === "coach" ? "/app/coach/profile" : activeRole === "parent" ? "/app/settings" : "/app/profile"}
                    onClick={() => setProfileMenuOpen(false)}
                    className="block px-4 py-4 bg-gradient-to-br from-indigo-500 to-purple-600 transition-opacity hover:opacity-95"
                  >
                    <div className="flex items-center gap-3">
                      {(user?.profile_url || user?.avatar_url) ? (
                        <img
                          src={user.profile_url || user.avatar_url || undefined}
                          alt={user.full_name || undefined}
                          className="object-cover w-12 h-12 border-2 rounded-full border-white/30"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-12 h-12 text-lg font-bold text-white rounded-full bg-white/20">
                          {getInitials(user?.full_name || "U")}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white truncate">{user?.full_name}</p>
                        <p className="text-xs truncate text-white/70">{user?.email}</p>
                      </div>
                      <svg className="w-5 h-5 text-white/70 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>

                  {/* Menu Items - Different for coaches, parents, and athletes */}
                  <div className="py-2">
                    <Link
                      to="/app/affiliate"
                      onClick={() => setProfileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-sm">Invită & câștigă</span>
                    </Link>
                    {activeRole === "coach" ? (
                      <>
                        <Link
                          to="/app/coach/athletes"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          <span className="text-sm">Sportivi</span>
                        </Link>
                        {/* Rapoarte lives here rather than in the bottom bar:
                            five tabs is what fits on a phone, and a monthly
                            report is not a daily tap. */}
                        <Link
                          to="/app/coach/reports"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6m4 6V7m4 10v-4M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span className="text-sm">Rapoarte</span>
                        </Link>
                        <Link
                          to="/app/coach/points"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                          <span className="text-sm">Puncte</span>
                        </Link>
                        <Link
                          to="/app/coach/payments"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          <span className="text-sm">Plăți</span>
                        </Link>
                        <Link
                          to="/app/coach/calendar"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="text-sm">Calendar locații</span>
                        </Link>
                        <Link
                          to="/app/coach/profile"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-sm">Profilul meu</span>
                        </Link>
                        <Link
                          to="/app/coach/settings"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-sm">Setări</span>
                        </Link>
                      </>
                    ) : activeRole === "parent" ? (
                      <>
                        <Link
                          to="/app/parent/payments"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          <span className="text-sm">Plăți</span>
                        </Link>
                        <Link
                          to="/app/parent/plans"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                          </svg>
                          <span className="text-sm">Planuri copii</span>
                        </Link>
                        {!activeChildIndividual && (
                        <Link
                          to="/app/parent/attendance"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <span className="text-sm">Istoric prezențe</span>
                        </Link>
                        )}
                        <Link
                          to="/app/settings"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-sm">Setări</span>
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/app/payments"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          <span className="text-sm">Plăți</span>
                        </Link>
                        <Link
                          to="/app/points"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm">Punctele mele</span>
                        </Link>
                        <Link
                          to="/app/attendance"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <span className="text-sm">Istoric prezențe</span>
                        </Link>
                        <Link
                          to="/app/leaderboard"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <span className="text-sm">Clasament</span>
                        </Link>
                        <Link
                          to="/app/settings"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-sm">Setări</span>
                        </Link>
                      </>
                    )}
                  </div>

                  {/* Every other role this account holds, not just club admin.
                      A coach who also trains in a squad — one email, two roles —
                      previously had no way to reach their own attendance,
                      points or leaderboard. Renders nothing for the single-role
                      accounts, which is almost everybody. */}
                  <RoleModeSwitcher onNavigate={() => setProfileMenuOpen(false)} />

                  {/* Logout */}
                  <div className="py-2 border-t border-slate-100">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors w-full"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span className="text-sm">Deconectare</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Pull-to-refresh indicator. Lives ABOVE main in z-stack so it
          appears at the top of the viewport during the gesture. Its
          transform / opacity are driven imperatively by usePullToRefresh
          to keep the gesture at 60fps without triggering React renders. */}
      <div
        id="ptr-indicator"
        className="fixed top-0 left-0 right-0 z-40 flex items-center justify-center pointer-events-none opacity-0"
        style={{ height: "60px", transform: "translateY(0px)" }}
      >
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>

      {/* Update-available banner. Self-detects native platform +
          compares installed build against /version/latest; renders
          nothing on web, on same-version installs, or after the user
          dismisses. Fixed just above the bottom nav so it can't get
          buried under scrolling content. */}
      <UpdateAvailableBanner />

      {/* Main Content — overscroll-contain stops the rubber-band bounce
          from propagating up to the (now hidden) outer container on
          iOS, so scrolling past the top/bottom feels natural but never
          shifts the fixed header/footer.
          Bottom padding scales with the Home Indicator inset via
          env(safe-area-inset-bottom). The 6rem base clears the nav
          bar's content row; the env() term adds the same offset the
          nav's own .safe-area-bottom padding uses, so on iPhones the
          last card ends above the taller (nav + home-indicator) chrome
          instead of being clipped by it. */}
      <main
        ref={mainScrollRef}
        className="flex-1 overflow-y-auto overscroll-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        style={{
          // -1px closes the sub-pixel seam that showed as a pale hairline
          // between the header's gradient and a page hero on fractional
          // device pixel ratios.
          marginTop: "-1px",
          // The bottom nav is hidden while typing, so its allowance would just
          // be dead space between the last field and the keyboard.
          paddingBottom: keyboardOpen ? "1rem" : "calc(6rem + env(safe-area-inset-bottom, 0px) + var(--android-nav-inset, 0px))",
        }}
      >
        {/* Their club's trial has ended: reads still work, writes do not, and
            saying so here beats a save button that silently does nothing. */}
        <ClubPausedNotice />
        {children}
      </main>

      {/* Bottom Navigation */}
      {/* Same gradient family as the header (lighter at the top edge,
          near-black at the bottom) so the two chrome bars read as one
          shell. It used to be a translucent #0b1120/85, which let page
          content bleed through and washed the labels out.
          Hidden while the keyboard is open: it would otherwise float over
          the field being typed into. */}
      {!keyboardOpen && (
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-gradient-to-b from-[#131a2e] to-[#080d18] border-white/[0.08] safe-area-bottom">
        <div className="relative flex items-stretch px-2">
          {/* Smooth sliding active indicator (no raised pill). */}
          {activeIndex >= 0 && (
            <span
              className="absolute top-0 h-[3px] w-[26px] rounded-b-full bg-gradient-to-r from-indigo-500 to-blue-500 shadow-[0_2px_10px_rgba(99,102,241,.7)] transition-[left] duration-300 ease-out"
              style={{
                left: `calc(${activeIndex} * (100% / ${navItems.length}) + (100% / ${navItems.length} / 2) - 13px)`,
              }}
            />
          )}
          {navItems.map(item => {
            // Check if current path matches the nav item
            const isActive = location.pathname === item.path ||
              (item.path !== "/app/dashboard" && item.path !== "/app/coach" && location.pathname.startsWith(item.path));

            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex flex-col items-center justify-center gap-1 flex-1 min-w-0 pt-2.5 pb-1 active:scale-90 transition-transform"
              >
                <NavIcon icon={item.icon} active={isActive} />
                <span className={`text-[9.5px] font-semibold truncate transition-colors ${
                  isActive ? "text-white" : "text-slate-300"
                }`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
      )}
    </div>
  );
}
